import { Injectable } from '@angular/core';
import { setDifference } from '@sama/js-core';
import { values } from '@sama/micro-dash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { IN_PROGRESS_INDEX } from '../../../../team-apis/core/reserved-shape-indexes';
import { CuboidState3D } from '../../../state/cuboid-state-3d';
import { interpolateLocation } from '../../../utils/point-cloud-interpolation-helper';
import { AbstractMeshService } from '../abstract-mesh.service';
import { haveShapeIdsChanged } from '../mesh-utils';
import { CuboidHelper } from './cuboid-helper';

@Injectable()
export class CuboidService extends AbstractMeshService<CuboidHelper> {
  focusedCuboid$!: Observable<CuboidHelper | undefined>;

  private cuboidCreated$ = new Subject<CuboidHelper>();

  constructor() {
    super();
    this.setUpSubscriptions();
  }

  private setUpSubscriptions() {
    this.subscribeToCuboidIds();
    this.focusedCuboid$ = this.buildFocusedCuboid$();
  }

  private subscribeToCuboidIds() {
    this.subscribeTo(
      this.renderStore('cuboids').$.pipe(
        distinctUntilChanged(haveShapeIdsChanged),
      ),
      (shapes) => {
        const shapeIds = values(shapes)
          .map((shape) => shape.index)
          .filter((index) => index !== IN_PROGRESS_INDEX);
        const updatedIds = new Set(shapeIds);
        const currentIds = new Set(this.helpers.keys());
        const cuboidsToAdd = setDifference(updatedIds, currentIds);
        const cuboidsToRemove = setDifference(currentIds, updatedIds);

        this.createCuboidHelpers(Array.from(cuboidsToAdd));
        this.removeCuboidHelpers(Array.from(cuboidsToRemove));
      },
    );
  }

  private createCuboidHelpers(indexes: number[]) {
    indexes.forEach((index) => {
      const cuboid = new CuboidHelper({
        index,
        isTraining: false,
      });
      this.addHelper(index, cuboid);
      this.cuboidCreated$.next(cuboid);
    });
  }

  private removeCuboidHelpers(indexes: number[]) {
    indexes.forEach((index) => {
      this.removeHelper(index);
    });
  }

  private buildFocusedCuboid$() {
    return combineLatest([
      // The whole cuboid object is needed to get the shape's index, otherwise
      // it would make more sense to subscribe to store.focusedCuboidLocation$
      this.store.focusedShape$,
      this.renderStore('frameNumber').$,
    ]).pipe(
      switchMap(([focusedShape]) => {
        if (!focusedShape) {
          return of(undefined);
        }
        if (this.helpers.has(focusedShape.index)) {
          // Return updated object immediately instead of waiting for the next
          // frame render. We can remove this when other classes no longer need
          // access to the helper object.
          this.updateCuboid(focusedShape);
          return of(this.getHelper(focusedShape.index));
        }
        return this.cuboidCreated$.pipe(
          filter((created) => created.index === focusedShape.index),
        );
      }),
    );
  }

  private updateCuboid(cuboid: CuboidState3D) {
    const { frameNumber } = this.renderStore.state();

    const location = interpolateLocation(cuboid.key_locations, frameNumber);
    this.getHelper(cuboid.index).updateLocation(location, this.store.state());
  }
}
